// NameGate — first-run "what's your name?" moment. Shows once, over everything,
// until the player has a name; then never again (playerName persists in the
// lifecycle store). Miko asks; the kid types a first name; LET'S GO.
//
// PRIVACY (children's product): first name only, capped at 16 chars, stored
// LOCALLY on the device. It is never sent to any server or included in the
// AI-brain calls — it exists purely so the game stops calling the player "Miko"
// and starts calling them by their own name.

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, Eyebrow, Button } from '../../ui/kit.js';
import { CartoonMiko } from '../../ui/cartoon.js';
import { useLife, useLifeDispatch } from '../../life/store.js';
import { useVoice } from '../../voice/VoiceContext.js';

export function NameGate() {
  const playerName = useLife().playerName;
  const dispatch = useLifeDispatch();
  const [value, setValue] = useState('');

  // MIKO SPEAKS — voice the ask. (Browsers may block audio before the first
  // tap on a cold load; speak() degrades silently, the written ask carries it.)
  const { speak } = useVoice();
  const show = !playerName;
  useEffect(() => {
    if (show) void speak("Yo! I'm Miko. What's your name, boss?");
  }, [show, speak]);

  if (!show) return null; // already introduced — gate never renders

  const submit = () => {
    const name = value.trim();
    if (!name) return;
    dispatch({ type: 'SET_PLAYER_NAME', name });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Enter your name"
      style={{
        position: 'fixed', inset: 0, zIndex: 98,
        display: 'grid', placeItems: 'center', padding: 18,
        background: 'color-mix(in srgb, var(--bg) 86%, transparent)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <motion.div
        initial={{ y: 14, opacity: 0, scale: 0.97 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 22 }}
        style={{ width: '100%', maxWidth: 360 }}
      >
        <Card style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
            <CartoonMiko size={72} mood="cheering" headphones />
          </div>
          <Eyebrow color="var(--brand)">YO! I'M MIKO. 🎧</Eyebrow>
          <h2 style={{ margin: '4px 0 4px', fontSize: 22, fontWeight: 900, color: 'var(--text)' }}>
            WHAT'S YOUR NAME, BOSS?
          </h2>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-2)', fontWeight: 700 }}>
            This is YOUR studio. Put your name on it.
          </p>

          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder="First name"
            maxLength={16}
            autoFocus
            autoComplete="off"
            aria-label="Your first name"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '13px 16px',
              borderRadius: 14, border: '2.5px solid var(--brand)',
              background: 'var(--surface-2)', color: 'var(--text)',
              fontFamily: 'inherit', fontSize: 18, fontWeight: 900, textAlign: 'center',
              outline: 'none', boxShadow: '0 0 14px color-mix(in srgb, var(--brand) 35%, transparent)',
            }}
          />

          <div style={{ marginTop: 14 }}>
            <Button tone="primary" size="lg" full disabled={!value.trim()} onClick={submit}>
              LET'S GO →
            </Button>
          </div>

          <p style={{ margin: '12px 0 0', fontSize: 11, color: 'var(--text-3)', fontWeight: 700 }}>
            Stays on this device. Never shared. 🔒
          </p>
        </Card>
      </motion.div>
    </div>
  );
}
